import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Plus, MapPin, Navigation, Eye, AlertTriangle, Newspaper, GitBranch, Share2, MoreVertical, ExternalLink, ChevronLeft, ChevronRight, Map as MapIcon, RefreshCw, Pencil, Download, Globe } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useAppData } from '../contexts/AppDataContext';
import { usePageHeader } from '../contexts/PageHeaderContext';
import { colors } from '../config/colors';
import { spacing } from '../config/spacing';
import { theme } from '../config/theme';
import { companyService } from '../services/companyService';
import { userCompaniesService } from '../services/userCompaniesService';
import { getStoredUserId } from '../services/authSession';
import { isCatalogForbiddenError } from '../services/supplierUserScope';
import { fetchUserScopedSupplier } from '../utils/userScopedSupplier';
import { watchlistService } from '../services/watchlistService';
import { riskAnalysisService } from '../services/riskAnalysisService';
import { newsService } from '../services/newsService';
import { analyzedNewsService } from '../services/analyzedNewsService';
import {
  collectEmbeddedRouteAnalyses,
  routesNeedingAnalysis,
} from '../utils/watchlistReadModelAdapter';
import { 
  CompanyDTO,
  RiskAnalysisDTO,
  createNewsArticle
} from '../models/companyModels';
import { 
  createRiskAnalysisRequest 
} from '../models/riskAnalysisModels';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/UI/card';
import { Button } from '../components/UI/button';
import { Badge } from '../components/UI/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/UI/tabs';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage } from '../components/UI/breadcrumb';
import {
  getRegionFromCountry,
  getCompanySizeType,
  formatRelativeTime,
  formatOverallRiskDisplay,
  normalizeSupplierTier,
  isValidCompanyWebsite,
  formatCompanyWebsiteHref,
} from '../utils/companyDisplayUtils';
import CompanySupplierProfileSection from '../components/Companies/CompanySupplierProfileSection';
import AddToWatchlistSimpleModal from '../components/Watchlist/AddToWatchlistSimpleModal';
import EditWatchlistItemModal from '../components/Watchlist/EditWatchlistItemModal';
import EditCompanyModal from '../components/Companies/EditCompanyModal';
import { downloadCompanyExport } from '../utils/exportCompanyData';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/UI/dropdown-menu';
import AddressManagementModal from '../components/Watchlist/AddressManagementModal';
import RouteManagementModal from '../components/Watchlist/RouteManagementModal';
import RoutesTabAddressCard from '../components/Watchlist/RoutesTabAddressCard';
import RouteDetailsDialog from '../components/Watchlist/RouteDetailsDialog';
import SupplyChainMap from '../components/Maps/SupplyChainMap';
import { useWatchlistMapData } from '../hooks/useWatchlistMapData';
import { getGoogleMapsRouteUrl } from '../utils/geocoding';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/UI/table';
import { 
  RiskAnalysisCard, 
  RiskAnalysisDetails, 
  RiskAnalysisButton, 
  RiskAnalysisModal 
} from '../components/RiskAnalysis';
import RiskIntelligenceFeed from '../components/RiskIntelligenceFeed/RiskIntelligenceFeed';
import { getSeverity, getArticleSentiment } from '../components/RiskIntelligenceFeed/RiskIntelligenceFeed';
import { 
  formatDistance as formatRouteDistance,
  formatCost as formatRouteCost
} from '../models/routeAnalysisModels';

const CompanyDetails = () => {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const { t } = useLocale();
  const { user } = useAuth();
  const { refreshWatchlist, watchlist, refreshUserCompanies } = useAppData();
  const { setPageHeader } = usePageHeader();
  const userId = user?.uid ?? null;
  
  // State management
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);
  const [watchlistItem, setWatchlistItem] = useState(null);
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [isLoadingCompany, setIsLoadingCompany] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    addresses: false,
    riskDetails: false,
    newsContent: {},
    watchlistAddresses: false,
    watchlistRoutes: false
  });
  
  // Risk Analysis State
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [riskAnalysisLoading, setRiskAnalysisLoading] = useState(false);
  const [showRiskAnalysisModal, setShowRiskAnalysisModal] = useState(false);
  const [riskAnalysisError, setRiskAnalysisError] = useState(null);
  
  // Modal states
  const [showAddToWatchlist, setShowAddToWatchlist] = useState(false);
  const [showEditWatchlist, setShowEditWatchlist] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [watchlistItemsForRouteModal, setWatchlistItemsForRouteModal] = useState([]);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [editCompanyLoading, setEditCompanyLoading] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [contactInfo, setContactInfo] = useState({
    phone: '',
    email: ''
  });
  const [localRouteUpdates, setLocalRouteUpdates] = useState({});
  const [isApplyingLocalUpdates, setIsApplyingLocalUpdates] = useState(false);
  const [updatingRoutes, setUpdatingRoutes] = useState(new Set());
  
  // Route Analysis State
  const [routeAnalyses, setRouteAnalyses] = useState({});
  const [analyzingRoutes, setAnalyzingRoutes] = useState({});
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);

  const routesList = watchlistItem?.userCompanyInfo?.routes ?? [];
  useEffect(() => {
    if (routesList.length > 0 && selectedRouteIndex >= routesList.length) {
      setSelectedRouteIndex(Math.max(0, routesList.length - 1));
    }
  }, [routesList.length, selectedRouteIndex]);

  const [mapRefreshTrigger, setMapRefreshTrigger] = useState(0);
  const [showRouteDetailsForRouteId, setShowRouteDetailsForRouteId] = useState(null);
  const mapRiskAnalyses = React.useMemo(() => {
    if (!ticker) return {};
    const score = company?.overallRiskScore ?? riskAnalysis?.overallRiskScore ?? company?.latestRiskAnalysis?.overallRiskScore;
    if (score != null && score > 0) return { [ticker]: { overallRiskScore: score } };
    return { [ticker]: { overallRiskScore: 0 } };
  }, [ticker, company, riskAnalysis]);

  const watchlistItemsForMap = useMemo(
    () => (watchlistItem ? [watchlistItem] : []),
    [watchlistItem]
  );
  const { markers, routes, bounds, geocoding } = useWatchlistMapData(
    watchlistItemsForMap,
    mapRiskAnalyses,
    mapRefreshTrigger
  );

  const [showRouteAnalysis, setShowRouteAnalysis] = useState(true);

  // Company Profile tab
  const [activeTab, setActiveTab] = useState('overview');

  // Company news for "News Analysis" tab (fetched separately; company payload may not include articles)
  const [companyNews, setCompanyNews] = useState([]);
  const [companyNewsLoading, setCompanyNewsLoading] = useState(false);
  // Sentiment filter for News Analysis: 'all' | 'positive' | 'negative' | 'neutral'
  const [newsSentimentFilter, setNewsSentimentFilter] = useState('all');
  const [newsTabQuickFilter, setNewsTabQuickFilter] = useState('all');
  const [newsTabSearch, setNewsTabSearch] = useState('');
  const [savedArticleIds, setSavedArticleIds] = useState(new Set());

  // Actual list we show (fetched news or company-embedded news); count in header must match this
  const displayNews = companyNews.length > 0 ? companyNews : (company?.newsArticles || []);
  // Filter by sentiment for News Analysis section
  const filteredNews = newsSentimentFilter === 'all'
    ? displayNews
    : displayNews.filter((article) => (article.sentiment || 'neutral').toLowerCase() === newsSentimentFilter);

  // Load saved article IDs so bookmark state is correct (shared with News page save API)
  useEffect(() => {
    if (!userId) {
      setSavedArticleIds(new Set());
      return;
    }
    newsService.getSavedArticles(userId).then((res) => {
      if (res.success && Array.isArray(res.data)) {
        const ids = res.data
          .map((a) => String(a?.articleId ?? a?.article_id ?? a?.id ?? '').trim())
          .filter(Boolean);
        setSavedArticleIds(new Set(ids));
      }
    }).catch(() => {});
  }, [userId]);

  const handleSaveArticle = useCallback(async (article) => {
    const rawId = article?.id ?? article?.articleId ?? article?.article_id;
    const id = rawId != null ? String(rawId).trim() : '';
    if (!id) {
      toast.error(t('failedToSaveArticle'));
      return;
    }
    if (!userId) {
      toast.error(t('failedToSaveArticle'));
      return;
    }
    const isSaved = savedArticleIds.has(id);
    const res = isSaved
      ? await newsService.unsaveArticle(id, userId)
      : await newsService.saveArticle(id, userId);
    if (res?.success) {
      setSavedArticleIds((prev) => {
        const next = new Set(prev);
        if (isSaved) next.delete(id);
        else next.add(id);
        return next;
      });
      toast.success(isSaved ? t('articleRemovedFromSaved') : t('articleSaved'));
    } else {
      toast.error(res?.message || (isSaved ? t('failedToUnsaveArticle') : t('failedToSaveArticle')));
    }
  }, [userId, savedArticleIds, t]);

  // Function to apply local route updates to fresh data
  const applyLocalRouteUpdates = (freshWatchlistItem) => {
    if (!freshWatchlistItem || !freshWatchlistItem.userCompanyInfo?.routes || Object.keys(localRouteUpdates).length === 0) {
      return freshWatchlistItem;
    }

    const updatedRoutes = freshWatchlistItem.userCompanyInfo.routes.map(route => {
      const rid = route.routeId ?? route.route_id;
      if (rid != null && localRouteUpdates[rid] !== undefined) {
        return { ...route, isActive: localRouteUpdates[rid] };
      }
      return route;
    });

    return {
      ...freshWatchlistItem,
      userCompanyInfo: {
        ...freshWatchlistItem.userCompanyInfo,
        routes: updatedRoutes
      }
    };
  };

  const fetchCompanyData = useCallback(async () => {
    if (!ticker) return;
    if (isLoadingCompany) return;

    setIsLoadingCompany(true);
    setLoading(true);
    try {
      const userId = user?.uid ?? getStoredUserId();
      const companyId = watchlistItem?.companyId ?? ticker;
      let loaded = null;

      if (isInWatchlist && userId) {
        loaded = await fetchUserScopedSupplier(userId, companyId);
      }

      if (!loaded) {
        const companyResponse = await companyService.getCompanyByTicker(
          companyId,
          isInWatchlist && userId ? userId : undefined
        );
        if (companyResponse.success) {
          loaded = companyResponse.data;
        } else {
          toast.error(companyResponse.error || t('companyLoadFailed'));
        }
      }

      if (loaded) {
        setCompany(loaded);
      }
    } catch (error) {
      toast.error(t('companyLoadFailed'));
    } finally {
      setLoading(false);
      setIsLoadingCompany(false);
    }
  }, [ticker, user?.uid, isInWatchlist, watchlistItem?.companyId, t]);

  const checkWatchlistStatus = useCallback(() => {
    const watchlistItemFromContext = (watchlist || []).find(
      (item) => (item.companyId ?? item.company_id)?.toUpperCase() === ticker?.toUpperCase()
    );
    if (!watchlistItemFromContext) {
      setWatchlistItem(null);
      setIsInWatchlist(false);
      return;
    }
    const itemWithRoutes = {
      ...watchlistItemFromContext,
      companyId: watchlistItemFromContext.companyId ?? watchlistItemFromContext.company_id ?? ticker,
    };
    const watchlistItemWithLocalUpdates = applyLocalRouteUpdates(itemWithRoutes);
    setWatchlistItem(watchlistItemWithLocalUpdates);
    setIsInWatchlist(true);
  }, [watchlist, ticker, localRouteUpdates]);

  useEffect(() => {
    checkWatchlistStatus();
  }, [checkWatchlistStatus]);

  // Fetch news for this company (News Analysis tab): analyzed-news API first, then fallbacks
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setCompanyNewsLoading(true);
    analyzedNewsService.getAnalyzedNewsForCompany(ticker).then((res) => {
      if (cancelled) return;
      if (res.success && res.data && res.data.length > 0) {
        setCompanyNews(res.data);
        setCompanyNewsLoading(false);
        return;
      }
      return companyService.getCompanyNews(ticker, 50).then((companyRes) => {
        if (cancelled) return;
        if (companyRes.success && companyRes.data && (Array.isArray(companyRes.data) ? companyRes.data.length : 1)) {
          const list = Array.isArray(companyRes.data) ? companyRes.data : [companyRes.data];
          setCompanyNews(list);
          setCompanyNewsLoading(false);
          return;
        }
        return newsService.getNewsByTicker(ticker, 1, 50).then((newsRes) => {
          if (cancelled) return;
          if (newsRes.success && newsRes.data && Array.isArray(newsRes.data) && newsRes.data.length > 0) {
            setCompanyNews(newsRes.data.map((article) => createNewsArticle(article)));
          } else {
            setCompanyNews([]);
          }
        }).catch(() => { if (!cancelled) setCompanyNews([]); });
      }).catch(() => {
        if (cancelled) return;
        newsService.getNewsByTicker(ticker, 1, 50).then((newsRes) => {
          if (cancelled) return;
          if (newsRes.success && newsRes.data && Array.isArray(newsRes.data) && newsRes.data.length > 0) {
            setCompanyNews(newsRes.data.map((article) => createNewsArticle(article)));
          } else {
            setCompanyNews([]);
          }
        }).catch(() => setCompanyNews([]));
      });
    }).catch(() => {
      if (cancelled) return;
      companyService.getCompanyNews(ticker, 50).then((res) => {
        if (cancelled) return;
        if (res.success && res.data && (Array.isArray(res.data) ? res.data.length : 1)) {
          setCompanyNews(Array.isArray(res.data) ? res.data : [res.data]);
        } else {
          setCompanyNews([]);
        }
      }).catch(() => setCompanyNews([]));
    }).finally(() => {
      if (!cancelled) setCompanyNewsLoading(false);
    });
    return () => { cancelled = true; };
  }, [ticker]);

  // Handle adding to watchlist
  const handleAddToWatchlist = async (watchlistData) => {
    try {
      const userIdFromStorage = localStorage.getItem('userId');
      const userDataFromStorage = localStorage.getItem('user');
      let userId = userIdFromStorage;
      if (!userId && userDataFromStorage) {
        try {
          const user = JSON.parse(userDataFromStorage);
          userId = user.uid;
        } catch (parseError) {
        }
      }
      if (!userId) {
        toast.error('User not authenticated');
        return;
      }
      const requestData = {
        company_id: ticker,
        notes: watchlistData.notes || '',
        priority: watchlistData.priority || 'normal',
        port_start_point: watchlistData.portStartPoint || '',
        port_end_point: watchlistData.portEndPoint || ''
      };
      const response = await watchlistService.addToWatchlist(userId, requestData);
      if (response.success) {
        toast.success(t('addedToWatchlist'));
        setShowAddToWatchlist(false);
        await refreshWatchlist();
        await checkWatchlistStatus();
      } else {
        toast.error(response.message || t('failedToAddToWatchlist'));
      }
    } catch (error) {
      toast.error(t('failedToAddToWatchlist'));
    }
  };

  // Handle removing from watchlist (backend soft-remove: sets removed: true; data kept)
  const handleRemoveFromWatchlist = async () => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const response = await watchlistService.removeFromWatchlist(userId, ticker);
      if (response.success) {
        toast.success(t('removedFromWatchlist'));
        setIsInWatchlist(false);
        setWatchlistItem(null);
        await refreshWatchlist();
      } else {
        toast.error(response.message || t('failedToRemoveFromWatchlist'));
      }
    } catch (error) {
      toast.error(t('failedToRemoveFromWatchlist'));
    }
  };

  // Handle updating watchlist item
  const handleUpdateWatchlistItem = async (updateData) => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      if (!userId) {
        toast.error('User not authenticated');
        return;
      }
      if (!watchlistItem?.companyId) {
        toast.error('Company ID not found');
        return;
      }
      const response = await watchlistService.updateWatchlistItem(userId, watchlistItem.companyId, updateData);
      if (response.success) {
        toast.success('Watchlist item updated successfully');
        setShowEditWatchlist(false);
        setWatchlistItem(response.data);
      } else {
        toast.error(response.message || 'Failed to update watchlist item');
      }
    } catch (error) {
      toast.error('Failed to update watchlist item');
    }
  };

  const handleToggleAlert = async () => {
    try {
      if (!watchlistItem) {
        toast.error('No watchlist item found');
        return;
      }
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const newAlertStatus = !watchlistItem.alertEnabled;
      const updateData = {
        alert_enabled: newAlertStatus,
        priority: watchlistItem.priority || 'normal',
        notes: watchlistItem.notes || '',
        port_start_point: watchlistItem.portStartPoint || '',
        port_end_point: watchlistItem.portEndPoint || ''
      };
      const response = await watchlistService.updateWatchlistItem(userId, watchlistItem.companyId, updateData);
      if (response.success) {
        // Update the local state immediately for better UX
        setWatchlistItem(prev => ({
          ...prev,
          alertEnabled: newAlertStatus
        }));
        toast.success(`Alerts ${newAlertStatus ? 'enabled' : 'disabled'}`);
      } else {
        toast.error(response.message || 'Failed to update alert status');
      }
    } catch (error) {
      toast.error('Failed to update alert status');
    }
  };

  const handleUpdateCompanyType = async (companyType) => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const response = await watchlistService.setCompanyType(userId, ticker, companyType);
      if (response.success) {
        // Refresh the watchlist item to get updated data
        await fetchCompanyData();
        toast.success('Company type updated');
      } else {
        toast.error(response.message || 'Failed to update company type');
      }
    } catch (error) {
      toast.error('Failed to update company type');
    }
  };

  const handleUpdateNotes = async (notes) => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const updateData = {
        notes: notes,
        priority: watchlistItem.priority,
        alert_enabled: watchlistItem.alertEnabled,
        port_start_point: watchlistItem.portStartPoint || '',
        port_end_point: watchlistItem.portEndPoint || ''
      };
      const response = await watchlistService.updateWatchlistItem(userId, watchlistItem.companyId, updateData);
      if (response.success) {
        // Update the local state immediately for better UX
        setWatchlistItem(prev => ({
          ...prev,
          notes: notes
        }));
        toast.success('Notes updated');
      } else {
        toast.error(response.message || 'Failed to update notes');
      }
    } catch (error) {
      toast.error('Failed to update notes');
    }
  };

  const handleSaveNotes = async () => {
    if (notesValue !== (watchlistItem?.notes || '')) {
      await handleUpdateNotes(notesValue);
    } else {
      toast.info('No changes to save');
    }
  };

  const handleSaveContactInfo = async () => {
    try {
      toast.success('Contact information saved successfully');
    } catch (error) {
      toast.error('Failed to save contact information');
    }
  };

  const handleOpenEditSupplier = async () => {
    if (!isInWatchlist) return;

    const userId = user?.uid ?? getStoredUserId();
    if (!userId) {
      toast.error(t('editSupplierNotAuthenticated'));
      return;
    }

    const companyId = watchlistItem?.companyId ?? ticker;
    setEditCompanyLoading(true);
    try {
      const scoped = await fetchUserScopedSupplier(userId, companyId);
      if (!scoped) {
        toast.error(t('editSupplierFailed'));
        return;
      }
      setEditCompany(scoped);
      setShowEditCompany(true);
    } finally {
      setEditCompanyLoading(false);
    }
  };

  const handleCloseEditSupplier = () => {
    setShowEditCompany(false);
    setEditCompany(null);
  };

  const handleUpdateCompanyProfile = async (formPayload) => {
    if (!isInWatchlist) return;

    const userId = user?.uid ?? getStoredUserId();
    if (!userId) {
      toast.error(t('editSupplierNotAuthenticated'));
      return;
    }

    const sourceCompany = editCompany || company;
    const updateTicker = String(
      sourceCompany?.ticker || sourceCompany?.companyId || watchlistItem?.companyId || ticker || ''
    ).trim();
    if (!updateTicker) {
      toast.error(t('editSupplierFailed'));
      return;
    }

    const payload = { ...formPayload, ticker: formPayload.ticker || updateTicker };

    try {
      const response = sourceCompany?.isUserMade
        ? await userCompaniesService.updateUserCompany(userId, updateTicker, payload)
        : await companyService.updateCompany(updateTicker, payload, userId);

      if (response.success && response.data) {
        setCompany(response.data);
        handleCloseEditSupplier();
        const savedTicker = response.data.ticker || updateTicker;
        if (savedTicker && savedTicker !== ticker) {
          navigate(`/companies/${encodeURIComponent(savedTicker)}`, { replace: true });
        } else {
          await fetchCompanyData();
        }
        await refreshUserCompanies();
        toast.success(t('editSupplierSuccess'));
        return;
      }

      if (isCatalogForbiddenError(response)) {
        toast.error(t('editSupplierForbiddenCatalog'));
      } else {
        toast.error(response.message || t('editSupplierFailed'));
      }
    } catch (error) {
      toast.error(error?.message || t('editSupplierFailed'));
    }
  };

  const handleExportCompanyData = () => {
    downloadCompanyExport(company, watchlistItem);
    toast.success(t('exportDataSuccess'));
  };

  const handleVisitWebsite = () => {
    const url = formatCompanyWebsiteHref(company?.website);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleUpdatePriority = async (newPriority) => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const updateData = {
        priority: newPriority,
        notes: watchlistItem.notes || '',
        alert_enabled: watchlistItem.alertEnabled,
        port_start_point: watchlistItem.portStartPoint || '',
        port_end_point: watchlistItem.portEndPoint || ''
      };
      const response = await watchlistService.updateWatchlistItem(userId, watchlistItem.companyId, updateData);
      if (response.success) {
        // Update the local state immediately for better UX
        setWatchlistItem(prev => ({
          ...prev,
          priority: newPriority
        }));
        toast.success(`Priority updated to ${newPriority.toUpperCase()}`);
      } else {
        toast.error(response.message || 'Failed to update priority');
      }
    } catch (error) {
      toast.error('Failed to update priority');
    }
  };

  // Address handlers
  const handleEditAddress = async (addressId, addressData) => {
    try {
      toast.success('Address updated successfully');
      await fetchCompanyData();
    } catch (error) {
      toast.error('Failed to update address');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const response = await watchlistService.removeUserAddress(userId, ticker, addressId);
      if (response.success) {
        toast.success('Address deleted successfully');
        await fetchCompanyData();
      } else {
        toast.error(response.message || 'Failed to delete address');
      }
    } catch (error) {
      toast.error('Failed to delete address');
    }
  };

  const handleSetPrimaryAddress = async (addressId) => {
    try {
      toast.success('Primary address updated successfully');
      await fetchCompanyData();
    } catch (error) {
      toast.error('Failed to set primary address');
    }
  };

  const handleEditRoute = async (routeId, routeData) => {
    try {
      toast.success('Route updated successfully');
      await fetchCompanyData();
    } catch (error) {
      toast.error('Failed to update route');
    }
  };

  const handleDeleteRoute = async (routeId) => {
    try {
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      const route = watchlistItem?.userCompanyInfo?.routes?.find(r => (r.routeId ?? r.route_id) === routeId);
      const ownerCompanyId = route?._ownerCompanyId ?? ticker;
      const response = await watchlistService.removeRoute(userId, ownerCompanyId, routeId);
      if (response.success) {
        toast.success('Route deleted successfully');
        await fetchCompanyData();
      } else {
        toast.error(response.message || 'Failed to delete route');
      }
    } catch (error) {
      toast.error('Failed to delete route');
    }
  };

  const handleToggleRouteStatus = async (routeId) => {
    try {
      if (!watchlistItem) {
        toast.error('No watchlist item found');
        return;
      }
      let userId = localStorage.getItem('userId');
      if (!userId) {
        try {
          const u = localStorage.getItem('user');
          if (u) userId = JSON.parse(u).uid;
        } catch (_) {}
      }
      if (!userId) {
        toast.error('User not authenticated');
        return;
      }
      const route = watchlistItem?.userCompanyInfo?.routes?.find(r => (r.routeId ?? r.route_id) === routeId);
      if (!route) {
        toast.error('Route not found');
        return;
      }
      if (updatingRoutes.has(routeId)) return;

      const newStatus = !route.isActive;
      const ownerCompanyId = route._ownerCompanyId ?? watchlistItem.companyId ?? ticker;

      // Add route to updating set
      setUpdatingRoutes(prev => new Set(prev).add(routeId));
      
      // Store the local route update
      setLocalRouteUpdates(prev => ({
        ...prev,
        [routeId]: newStatus
      }));
      
      // Update the route status in the local state for immediate UI feedback
      const updatedRoutes = watchlistItem.userCompanyInfo.routes.map(r => 
        (r.routeId ?? r.route_id) === routeId ? { ...r, isActive: newStatus } : r
      );
      
      const updatedUserCompanyInfo = {
        ...watchlistItem.userCompanyInfo,
        routes: updatedRoutes
      };
      
      const updatedWatchlistItem = {
        ...watchlistItem,
        userCompanyInfo: updatedUserCompanyInfo
      };
      
      setWatchlistItem(updatedWatchlistItem);

      const response = await watchlistService.updateRouteStatus(userId, ownerCompanyId, routeId, newStatus);
      if (response.success) {
        toast.success(`Route ${newStatus ? 'activated' : 'deactivated'} successfully`);
        
        // Clear the local route update since it's now persisted
        setLocalRouteUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[routeId];
          return newUpdates;
        });
      } else {
        // Revert the local state change
        const revertedRoutes = watchlistItem.userCompanyInfo.routes.map(r => 
          (r.routeId ?? r.route_id) === routeId ? { ...r, isActive: !newStatus } : r
        );
        const revertedUserCompanyInfo = {
          ...watchlistItem.userCompanyInfo,
          routes: revertedRoutes
        };
        const revertedWatchlistItem = {
          ...watchlistItem,
          userCompanyInfo: revertedUserCompanyInfo
        };
        setWatchlistItem(revertedWatchlistItem);
        
        // Clear the local route update since it failed
        setLocalRouteUpdates(prev => {
          const newUpdates = { ...prev };
          delete newUpdates[routeId];
          return newUpdates;
        });
        
        toast.error(response.message || 'Failed to update route status');
      }
    } catch (error) {
      // Revert the local state change on error
      if (watchlistItem) {
        const route = watchlistItem?.userCompanyInfo?.routes?.find(r => (r.routeId ?? r.route_id) === routeId);
        if (route) {
          const revertedRoutes = watchlistItem.userCompanyInfo.routes.map(r => 
            (r.routeId ?? r.route_id) === routeId ? { ...r, isActive: route.isActive } : r
          );
          const revertedUserCompanyInfo = {
            ...watchlistItem.userCompanyInfo,
            routes: revertedRoutes
          };
          const revertedWatchlistItem = {
            ...watchlistItem,
            userCompanyInfo: revertedUserCompanyInfo
          };
          setWatchlistItem(revertedWatchlistItem);
        }
      }
      
      // Clear the local route update since it failed
      setLocalRouteUpdates(prev => {
        const newUpdates = { ...prev };
        delete newUpdates[routeId];
        return newUpdates;
      });
      
      toast.error('Failed to update route status. Please try again.');
    } finally {
      // Remove route from updating set
      setUpdatingRoutes(prev => {
        const newSet = new Set(prev);
        newSet.delete(routeId);
        return newSet;
      });
    }
  };

  // Route Analysis Functions (use _ownerCompanyId for inter-company routes: analysis is under start company)
  const fetchRouteAnalyses = async () => {
    if (!watchlistItem?.userCompanyInfo?.routes || watchlistItem.userCompanyInfo.routes.length === 0) {
      return;
    }

    const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
    if (!userId) return;

    const embedded = collectEmbeddedRouteAnalyses([watchlistItem]);
    setRouteAnalyses(embedded);

    const missing = routesNeedingAnalysis([watchlistItem]);
    if (missing.length === 0) return;

    const result = await watchlistService.getRouteAnalysesBatch(userId, missing);
    if (!result.success || !Array.isArray(result.data)) return;

    setRouteAnalyses((prev) => {
      const analysesMap = { ...prev };
      result.data.forEach((analysis) => {
        const routeId = analysis.routeId ?? analysis.route_id;
        if (routeId != null) {
          analysesMap[routeId] = analysis;
        }
      });
      return analysesMap;
    });
  };

  const handleAnalyzeRoute = async (routeId) => {
    try {
      setAnalyzingRoutes(prev => ({ ...prev, [routeId]: true }));
      const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
      if (!userId) {
        toast.error('Please log in to analyze route');
        return;
      }
      const route = watchlistItem?.userCompanyInfo?.routes?.find(
        r => (r.routeId ?? r.route_id) === routeId
      );
      const ownerCompanyId = route?._ownerCompanyId ?? ticker;
      const result = await watchlistService.getRouteAnalysis(userId, ownerCompanyId, routeId);
      if (result.success && result.data) {
        setRouteAnalyses(prev => ({ ...prev, [routeId]: result.data }));
        toast.success('Route analysis updated successfully');
      } else {
        toast.error(result.message || 'Failed to analyze route');
      }
    } catch (error) {
      toast.error('Failed to analyze route');
    } finally {
      setAnalyzingRoutes(prev => ({ ...prev, [routeId]: false }));
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  // Set header title "Company Details" and description to company name
  useEffect(() => {
    if (company?.name != null) {
      setPageHeader({ title: t('companyDetailsTitle'), description: company.name });
    } else {
      setPageHeader(null);
    }
    return () => setPageHeader(null);
  }, [company?.name, t, setPageHeader]);

  // Fetch route analyses when watchlist item is loaded
  const routeAnalysisKey = React.useMemo(
    () => (watchlistItem?.userCompanyInfo?.routes ?? [])
      .map((route) => `${route._ownerCompanyId ?? ticker}|${route.routeId ?? route.route_id}`)
      .sort()
      .join(','),
    [watchlistItem?.userCompanyInfo?.routes, ticker]
  );

  useEffect(() => {
    if (watchlistItem && isInWatchlist) {
      fetchRouteAnalyses();
    } else {
      setRouteAnalyses({});
    }
  }, [isInWatchlist, routeAnalysisKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update notesValue and contactInfo when watchlistItem changes
  useEffect(() => {
    if (watchlistItem) {
      setNotesValue(watchlistItem.notes || '');
      // Initialize contact info (this would come from the API in a real implementation)
      setContactInfo({
        phone: watchlistItem.userCompanyInfo?.phone || '',
        email: watchlistItem.userCompanyInfo?.email || ''
      });
    }
  }, [watchlistItem]);

  // When route modal opens, fetch full watchlist so modal can offer "end at another company"
  useEffect(() => {
    if (!showRouteModal) return;
    setWatchlistItemsForRouteModal(Array.isArray(watchlist) ? watchlist : []);
  }, [showRouteModal, watchlist]);

  // Note: Local route updates are now applied directly in fetchCompanyData() 
  // to prevent infinite loops and ensure updates persist through data refreshes


  // Load risk analysis when company data is available
  useEffect(() => {
    if (company?.latestRiskAnalysis) {
      setRiskAnalysis(company.latestRiskAnalysis);
    }
  }, [company]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: colors.background.primary }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: colors.background.primary }}>
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: colors.text.primary }}>{t('companyNotFound')}</h2>
          <p className="mb-4 text-sm" style={{ color: colors.text.secondary }}>{t('companyNotFoundMessage', { ticker })}</p>
          <Button onClick={() => navigate('/companies')}>{t('backToCompanies')}</Button>
        </div>
      </div>
    );
  }

  // Helper functions
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleNewsContent = (articleId) => {
    setExpandedSections(prev => ({
      ...prev,
      newsContent: {
        ...prev.newsContent,
        [articleId]: !prev.newsContent[articleId]
      }
    }));
  };

  // Company Logo Component based on Industry
  const CompanyLogo = ({ industry, companyName }) => {
    const getIndustryIcon = (industry) => {
      const industryLower = industry?.toLowerCase() || '';
      
      if (industryLower.includes('technology') || industryLower.includes('tech') || industryLower.includes('software')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      } else if (industryLower.includes('manufacturing') || industryLower.includes('industrial')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        );
      } else if (industryLower.includes('automotive') || industryLower.includes('transportation')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2v0a2 2 0 01-2-2v-4a2 2 0 00-2-2H8z" />
          </svg>
        );
      } else if (industryLower.includes('healthcare') || industryLower.includes('medical') || industryLower.includes('pharmaceutical')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      } else if (industryLower.includes('energy') || industryLower.includes('oil') || industryLower.includes('gas') || industryLower.includes('power')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      } else if (industryLower.includes('finance') || industryLower.includes('banking') || industryLower.includes('financial')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
          </svg>
        );
      } else if (industryLower.includes('retail') || industryLower.includes('commerce') || industryLower.includes('consumer')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        );
      } else if (industryLower.includes('agriculture') || industryLower.includes('food') || industryLower.includes('farming')) {
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        );
      } else {
        // Default company icon
        return (
          <svg className="w-8 h-8" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        );
      }
    };

    return (
      <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f3f0ff' }}>
        {getIndustryIcon(industry)}
      </div>
    );
  };

  // Circular Gauge Component
  const CircularGauge = ({ score, label, color = 'indigo', size = 'lg' }) => {
    const percentage = Math.min(100, Math.max(0, score || 0));
    const radius = size === 'lg' ? 60 : 45;
    const circumference = 2 * Math.PI * radius;
    const strokeDasharray = circumference;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;
    
    const colorClasses = {
      indigo: 'stroke-indigo-600',
      red: 'stroke-red-600',
      orange: 'stroke-orange-600',
      yellow: 'stroke-yellow-600',
      green: 'stroke-green-600',
      blue: 'stroke-blue-600'
    };

    const sizeClasses = {
      lg: 'w-32 h-32',
      md: 'w-24 h-24',
      sm: 'w-16 h-16'
    };

    return (
      <div className="flex flex-col items-center">
        <div className={`relative ${sizeClasses[size]}`}>
          <svg className={`${sizeClasses[size]} transform -rotate-90`} viewBox={`0 0 ${radius * 2 + 20} ${radius * 2 + 20}`}>
            <circle
              cx={radius + 10}
              cy={radius + 10}
              r={radius}
              stroke="#e5e7eb"
              strokeWidth="8"
              fill="transparent"
            />
            <circle
              cx={radius + 10}
              cy={radius + 10}
              r={radius}
              strokeWidth="8"
              fill="transparent"
              strokeDasharray={strokeDasharray}
              strokeDashoffset={strokeDashoffset}
              className={colorClasses[color]}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`font-bold text-gray-900 ${size === 'lg' ? 'text-2xl' : 'text-lg'}`}>
              {Math.round(percentage)}
            </span>
          </div>
        </div>
        <span className="text-sm font-medium text-gray-600 mt-2 text-center">{label}</span>
      </div>
    );
  };

  // Risk Score Card Component
  const RiskScoreCard = ({ riskScore }) => {
    const scoreColors = riskScore.getScoreColor();
    const impactColors = riskScore.getImpactLevelColor();
    const confidenceColor = riskScore.getConfidenceColor();

    return (
      <div className={`p-4 rounded-lg border ${scoreColors.bg} ${scoreColors.border}`}>
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-gray-900">{riskScore.riskName}</h4>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${impactColors.bg} ${impactColors.text}`}>
            {riskScore.impactLevel.toUpperCase()}
          </span>
        </div>
        
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className={`text-2xl font-bold ${scoreColors.text}`}>
              {riskScore.score}
            </span>
            <span className="text-sm text-gray-600">/100</span>
          </div>
          <div className="text-right">
            <div className="text-xs text-gray-500">Confidence</div>
            <div className={`text-sm font-medium ${confidenceColor}`}>
              {riskScore.confidence}%
            </div>
          </div>
        </div>

        {riskScore.reasoning && (
          <p className="text-sm text-gray-600 mb-2">{riskScore.reasoning}</p>
        )}

        {expandedSections.riskDetails && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            {riskScore.evidence.length > 0 && (
              <div className="mb-3">
                <h5 className="text-sm font-medium text-gray-900 mb-1">Evidence</h5>
                <ul className="text-xs text-gray-600 space-y-1">
                  {riskScore.evidence.map((evidence, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-1">•</span>
                      <span>{evidence}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {riskScore.mitigationSuggestions.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-gray-900 mb-1">Mitigation Suggestions</h5>
                <ul className="text-xs text-gray-600 space-y-1">
                  {riskScore.mitigationSuggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="mr-1">•</span>
                      <span>{suggestion}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // News Article Card Component
  const NewsArticleCard = ({ article }) => {
    const sentimentColors = article.getSentimentColor();
    const isExpanded = expandedSections.newsContent[article.articleId];

    return (
      <div className={`p-4 rounded-lg border ${sentimentColors.bg} ${sentimentColors.border} hover:shadow-md transition-shadow`}>
        <div className="flex items-start justify-between mb-2">
          <h4 className="font-semibold text-gray-900 flex-1 mr-2">
            <a 
              href={article.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-indigo-600 transition-colors"
            >
              {article.title}
            </a>
          </h4>
          <span className={`px-2 py-1 text-xs font-medium rounded-full ${sentimentColors.bg} ${sentimentColors.text} border ${sentimentColors.border}`}>
            {article.sentiment.toUpperCase()}
          </span>
        </div>
        
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>{article.source}</span>
          <span>{article.getFormattedDate()}</span>
        </div>

        <p className="text-sm text-gray-700 mb-3">
          {isExpanded ? article.description : `${article.description.substring(0, 150)}...`}
        </p>

        {article.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {article.keywords.slice(0, 3).map((keyword, idx) => (
              <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                {keyword}
              </span>
            ))}
            {article.keywords.length > 3 && (
              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded">
                +{article.keywords.length - 3} more
              </span>
            )}
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Relevance: {article.relevanceScore}%
          </span>
          <button
            onClick={() => toggleNewsContent(article.articleId)}
            className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
          >
            {isExpanded ? 'Show Less' : 'Read More'}
          </button>
        </div>
      </div>
    );
  };

  // Address Card Component
  const AddressCard = ({ address, onEdit, onDelete, onSetPrimary }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
      streetAddress: address.streetAddress,
      city: address.city,
      stateProvince: address.stateProvince,
      postalCode: address.postalCode,
      country: address.country
    });

    const handleSave = () => {
      onEdit(address.addressId, editData);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditData({
        streetAddress: address.streetAddress,
        city: address.city,
        stateProvince: address.stateProvince,
        postalCode: address.postalCode,
        country: address.country
      });
      setIsEditing(false);
    };

    return (
      <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
        {/* Location Icon */}
        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: '#f3f0ff' }}>
          <svg className="w-4 h-4" style={{ color: colors.text.accent }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>

        {/* Address Content */}
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={editData.streetAddress}
                onChange={(e) => setEditData({...editData, streetAddress: e.target.value})}
                placeholder="Street Address"
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-opacity-50"
                style={{ 
                  borderColor: '#E6E9EE',
                  focusRingColor: colors.text.accent
                }}
              />
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={editData.city}
                  onChange={(e) => setEditData({...editData, city: e.target.value})}
                  placeholder="City"
                  className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    borderColor: '#E6E9EE',
                    focusRingColor: colors.text.accent
                  }}
                />
                <input
                  type="text"
                  value={editData.stateProvince}
                  onChange={(e) => setEditData({...editData, stateProvince: e.target.value})}
                  placeholder="State/Province"
                  className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    borderColor: '#E6E9EE',
                    focusRingColor: colors.text.accent
                  }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={editData.postalCode}
                  onChange={(e) => setEditData({...editData, postalCode: e.target.value})}
                  placeholder="Postal Code"
                  className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    borderColor: '#E6E9EE',
                    focusRingColor: colors.text.accent
                  }}
                />
                <input
                  type="text"
                  value={editData.country}
                  onChange={(e) => setEditData({...editData, country: e.target.value})}
                  placeholder="Country"
                  className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-opacity-50"
                  style={{ 
                    borderColor: '#E6E9EE',
                    focusRingColor: colors.text.accent
                  }}
                />
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={handleSave}
                  className="px-3 py-1 text-sm font-medium rounded-lg transition-colors"
                  style={{ 
                    backgroundColor: '#1ABC9C',
                    color: 'white'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#0d9488'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#1ABC9C'}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className="px-3 py-1 text-sm font-medium rounded-lg border transition-colors"
                  style={{ 
                    borderColor: '#E6E9EE',
                    color: '#6B7280'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <p className="text-sm font-medium truncate" style={{ color: '#0F172A' }}>
                  {address.streetAddress}
                </p>
                {address.isPrimary && (
                  <span className="px-2 py-1 text-xs font-medium rounded-full flex-shrink-0" style={{ 
                    backgroundColor: '#f0fdfa', 
                    color: '#1ABC9C' 
                  }}>
                    Primary
                  </span>
                )}
              </div>
              <p className="text-xs truncate" style={{ color: '#6B7280' }}>
                {address.city}, {address.stateProvince} {address.postalCode}
              </p>
              <p className="text-xs truncate" style={{ color: '#6B7280' }}>
                {address.country}
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        {!isEditing && (
          <div className="flex items-center space-x-1">
            {/* Map Preview Button */}
            <button
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="View on map"
            >
              <svg className="w-4 h-4" style={{ color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
              </svg>
            </button>

            {/* Edit Button */}
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-1 rounded hover:bg-gray-100 transition-colors"
              title="Edit address"
            >
              <svg className="w-4 h-4" style={{ color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>

            {/* More Actions Dropdown */}
            <div className="relative">
              <button className="p-1 rounded hover:bg-gray-100 transition-colors">
                <svg className="w-4 h-4" style={{ color: '#6B7280' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </button>
              {/* Dropdown menu would go here */}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Route Card Component
  const RouteCard = ({ route, onEdit, onDelete, onToggleStatus, isUpdating }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
      routeName: route.routeName,
      description: route.description
    });

    const handleSave = () => {
      onEdit(route.routeId, editData);
      setIsEditing(false);
    };

    const handleCancel = () => {
      setEditData({
        routeName: route.routeName,
        description: route.description
      });
      setIsEditing(false);
    };

    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3" />
              </svg>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                route.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {route.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            
            {isEditing ? (
              <div className="space-y-3">
                <input
                  type="text"
                  value={editData.routeName}
                  onChange={(e) => setEditData({...editData, routeName: e.target.value})}
                  placeholder="Route Name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <textarea
                  value={editData.description}
                  onChange={(e) => setEditData({...editData, description: e.target.value})}
                  placeholder="Route Description"
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={handleSave}
                    className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancel}
                    className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h4 className="font-medium text-gray-900">{route.routeName}</h4>
                {route.description && (
                  <p className="text-sm text-gray-600 mt-1">{route.description}</p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                  <span>Created: {new Date(route.createdDate).toLocaleDateString()}</span>
                  <span>Distance: N/A</span>
                  <span>Avg Time: N/A</span>
                </div>
              </div>
            )}
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setIsEditing(!isEditing)}
              className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
              title="Edit route"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button
              onClick={() => {
                if (!isUpdating) {
                  onToggleStatus(route.routeId);
                }
              }}
              disabled={isUpdating}
              className={`px-2 py-1 text-xs rounded transition-colors flex items-center space-x-1 ${
                isUpdating 
                  ? 'bg-gray-100 text-gray-500 cursor-not-allowed' 
                  : route.isActive 
                    ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' 
                    : 'bg-green-100 text-green-800 hover:bg-green-200'
              }`}
            >
              {isUpdating && (
                <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              )}
              <span>{isUpdating ? 'Updating...' : (route.isActive ? 'Deactivate' : 'Activate')}</span>
            </button>
            <button
              onClick={() => onDelete(route.routeId)}
              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
              title="Delete route"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Risk Analysis Functions
  const handleRiskAnalysisComplete = (analysis, fromCache) => {
    setRiskAnalysis(analysis);
    setRiskAnalysisError(null);
    toast.success(`Risk analysis ${fromCache ? 'loaded from cache' : 'completed successfully'}`);
  };

  const handleRiskAnalysisError = (error) => {
    setRiskAnalysisError(error);
    toast.error(`Risk analysis failed: ${error}`);
  };

  const handleOpenRiskAnalysisModal = () => {
    setShowRiskAnalysisModal(true);
  };

  const handleCloseRiskAnalysisModal = () => {
    setShowRiskAnalysisModal(false);
  };

  const riskAnalysisData = riskAnalysis || company?.latestRiskAnalysis;
  const scoreDisplay = formatOverallRiskDisplay(company?.overallRiskScore, riskAnalysisData);
  const supplierTier = normalizeSupplierTier(company?.tier);
  const supplierTierLabel = supplierTier ? t(`createSupplierTier${supplierTier}`) : null;
  const riskLevelKey = riskAnalysisData ? (new RiskAnalysisDTO(riskAnalysisData).getOverallRiskLevel()) : '—';
  const region = getRegionFromCountry(company?.country);
  const locationLine = [company?.country].filter(Boolean).join(', ');
  const locationWithRegion = region ? `${locationLine} • ${region}` : locationLine || '—';
  const lastUpdatedRt = company?.updatedAt ? formatRelativeTime(company.updatedAt) : null;
  const lastUpdatedText = lastUpdatedRt?.relativeKey ? t(lastUpdatedRt.relativeKey, lastUpdatedRt.relativeParams) : (company?.updatedAt ? new Date(company.updatedAt).toLocaleDateString() : '—');
  const sizeType = getCompanySizeType(company?.employeeCount);
  const sizeLabel = sizeType === 'large' ? t('companyTypeLargeEnterprise') : sizeType === 'medium' ? t('companyTypeMediumEnterprise') : sizeType === 'small' ? t('companyTypeSmallEnterprise') : null;
  const individualScores = riskAnalysisData?.individualRiskScores || [];
  const riskDriversCount = individualScores.length;
  const operationalScore = riskAnalysisData?.individualRiskScores?.find(r => (r.category || '').toLowerCase().includes('operational'))?.score ?? riskAnalysisData?.supplyChainRiskScore ?? 0;
  const financialScore = riskAnalysisData?.individualRiskScores?.find(r => (r.category || '').toLowerCase().includes('financial'))?.score ?? 0;
  const complianceScore = riskAnalysisData?.individualRiskScores?.find(r => (r.category || '').toLowerCase().includes('compliance'))?.score ?? riskAnalysisData?.industryRiskScore ?? 0;

  const getScoreColor = (s) => {
    const v = (s || 0) / 10;
    if (v >= 7) return colors.danger[500];
    if (v >= 4) return colors.warning[500];
    return colors.success[500];
  };

  return (
    <div className="min-h-screen pt-0 -mt-2" style={{ backgroundColor: colors.background.primary || colors.mainContent?.bg }}>
      <div className="space-y-4">
        <Breadcrumb className="mb-1">
          <BreadcrumbItem>
            <BreadcrumbLink to="/companies">{t('breadcrumbCompanies')}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>{company.name}</BreadcrumbPage>
          </BreadcrumbItem>
        </Breadcrumb>

        <Card className="p-6" style={{ boxShadow: spacing.shadow?.lg, borderColor: colors.border.light }}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: colors.primary[50] }}>
                <CompanyLogo industry={company.industry} companyName={company.name} />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-2xl font-semibold" style={{ color: colors.text.primary }}>{company.name}</h1>
                  {scoreDisplay != null ? (
                    <span className="text-lg font-semibold" style={{ color: colors.danger[500] }}>
                      {scoreDisplay} / 10
                    </span>
                  ) : (
                    <span className="text-sm font-medium text-text-secondary">{t('noRiskAnalysis')}</span>
                  )}
                  {scoreDisplay != null && (
                    <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>
                      {riskLevelKey === 'High' || riskLevelKey === 'Critical' ? t('highRisk') : riskLevelKey}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {company.ticker ? (
                    <Badge variant="outline" className="font-mono text-xs">
                      {company.ticker}
                    </Badge>
                  ) : null}
                  {supplierTierLabel ? <Badge variant="secondary">{supplierTierLabel}</Badge> : null}
                  {company.industry && <Badge variant="secondary">{company.industry}</Badge>}
                  {sizeLabel && <Badge variant="secondary">{sizeLabel}</Badge>}
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm" style={{ color: colors.text.secondary }}>
                  <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{locationWithRegion}</span>
                  <span className="flex items-center gap-1">{t('lastUpdated')}: {lastUpdatedText}</span>
                  {(company.employeeCount != null && company.employeeCount > 0) && (
                    <span className="flex items-center gap-1">{t('employeesCount', { count: (typeof company.getFormattedEmployeeCount === 'function' ? company.getFormattedEmployeeCount() : null) || company.employeeCount })}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isInWatchlist ? (
                <Button variant="destructive" size="default" onClick={() => setShowRemoveConfirmation(true)}>{t('removeFromWatchlist')}</Button>
              ) : (
                <Button variant="default" size="default" onClick={() => setShowAddToWatchlist(true)}>{t('addToWatchlist')}</Button>
              )}
              <Button variant="ghost" size="icon" aria-label={t('share')}><Share2 className="h-4 w-4" /></Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label={t('companyActionsMenu')}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {isInWatchlist ? (
                    <DropdownMenuItem
                      onClick={handleOpenEditSupplier}
                      disabled={editCompanyLoading}
                    >
                      <Pencil className="h-4 w-4" />
                      {editCompanyLoading ? t('editSupplierLoading') : t('actionEditSupplier')}
                    </DropdownMenuItem>
                  ) : null}
                  {isInWatchlist && watchlistItem ? (
                    <DropdownMenuItem onClick={() => setShowEditWatchlist(true)}>
                      <Eye className="h-4 w-4" />
                      {t('actionEditWatchlist')}
                    </DropdownMenuItem>
                  ) : null}
                  {isValidCompanyWebsite(company.website) ? (
                    <DropdownMenuItem onClick={handleVisitWebsite}>
                      <Globe className="h-4 w-4" />
                      {t('visitWebsite')}
                    </DropdownMenuItem>
                  ) : null}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleExportCompanyData}>
                    <Download className="h-4 w-4" />
                    {t('exportData')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-full">
          <TabsList variant="pill" className="w-full mb-4">
            <TabsTrigger value="overview" icon={Eye} variant="pill">{t('tabOverview')}</TabsTrigger>
            <TabsTrigger value="risk" icon={AlertTriangle} variant="pill">{t('tabRisk')}</TabsTrigger>
            <TabsTrigger value="news" icon={Newspaper} variant="pill">{t('tabNews')}</TabsTrigger>
            <TabsTrigger value="routes" icon={GitBranch} variant="pill">{t('tabRoutes')}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-4 w-full max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card className="p-6" style={{ borderColor: colors.border.light, backgroundColor: colors.background.card }}>
                <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text.primary }}>{t('companyOverview')}</h3>
                <CompanySupplierProfileSection company={company} />
              </Card>
              <Card className="p-6" style={{ borderColor: colors.border.light }}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>{t('riskScore')}</h3>
                  <RiskAnalysisButton ticker={ticker} companyName={company.name} onAnalysisComplete={handleRiskAnalysisComplete} onError={handleRiskAnalysisError} variant="primary" />
                </div>
                {!riskAnalysisData ? (
                  <p className="text-sm py-4" style={{ color: colors.text.secondary }}>{t('noRiskAnalysis')}</p>
                ) : (
                  <div className="space-y-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-bold" style={{ color: getScoreColor(riskAnalysisData.overallRiskScore) }}>{(riskAnalysisData.overallRiskScore / 10).toFixed(1)}</span>
                      <span className="text-lg" style={{ color: colors.text.secondary }}>/ 10</span>
                      <span className="text-xs font-medium ml-2 px-2 py-0.5 rounded" style={{ backgroundColor: colors.primary[50], color: colors.primary[700] }}>{riskAnalysisData.getFormattedAnalysisDate?.() || new Date(riskAnalysisData.analysisDate).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm" style={{ color: colors.text.secondary }}>{riskLevelKey === 'High' || riskLevelKey === 'Critical' ? t('riskScoreRequiresAttention') : riskLevelKey === 'Medium' ? t('riskScoreMediumRisk') : t('riskScoreLowRisk')}</p>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: colors.gray[50] }}>
                        <div className="text-xs font-medium mb-0.5" style={{ color: colors.text.secondary }}>{t('supplyChainRisk')}</div>
                        <div className="text-lg font-semibold" style={{ color: getScoreColor(riskAnalysisData.supplyChainRiskScore) }}>{(riskAnalysisData.supplyChainRiskScore / 10).toFixed(1)}</div>
                      </div>
                      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: colors.gray[50] }}>
                        <div className="text-xs font-medium mb-0.5" style={{ color: colors.text.secondary }}>{t('industryRisk')}</div>
                        <div className="text-lg font-semibold" style={{ color: getScoreColor(riskAnalysisData.industryRiskScore) }}>{(riskAnalysisData.industryRiskScore / 10).toFixed(1)}</div>
                      </div>
                      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: colors.gray[50] }}>
                        <div className="text-xs font-medium mb-0.5" style={{ color: colors.text.secondary }}>{t('newsSentimentLabel')}</div>
                        <div className="text-lg font-semibold" style={{ color: getScoreColor(riskAnalysisData.newsSentimentScore) }}>{(riskAnalysisData.newsSentimentScore / 10).toFixed(1)}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 py-2 border-t border-b" style={{ borderColor: colors.border.light }}>
                      <span className="text-xs" style={{ color: colors.text.secondary }}>{t('analyzedArticlesCount', { count: riskAnalysisData.analyzedNewsCount || 0 })}</span>
                      <span className="text-xs" style={{ color: colors.success[600] }}>+{riskAnalysisData.positiveNewsCount || 0}</span>
                      <span className="text-xs" style={{ color: colors.danger[600] }}>−{riskAnalysisData.negativeNewsCount || 0}</span>
                      <span className="text-xs" style={{ color: colors.text.tertiary }}>○{riskAnalysisData.neutralNewsCount || 0}</span>
                      <span className="text-xs" style={{ color: colors.text.secondary }}>Confidence: <strong style={{ color: colors.text.primary }}>{Math.round(riskAnalysisData.analysisConfidence || 0)}%</strong></span>
                      <span className="text-xs" style={{ color: colors.text.secondary }}>Data quality: <strong style={{ color: colors.text.primary }}>{Math.round(riskAnalysisData.dataQualityScore || 0)}%</strong></span>
                    </div>
                    {riskAnalysisData.analysisSummary && (
                      <div>
                        <div className="text-xs font-medium mb-1" style={{ color: colors.text.secondary }}>{t('summaryLabel')}</div>
                        <p className="text-sm leading-relaxed" style={{ color: colors.text.primary }}>{riskAnalysisData.analysisSummary}</p>
                      </div>
                    )}
                    {riskAnalysisData.keyFindings && riskAnalysisData.keyFindings.length > 0 && (
                      <div>
                        <div className="text-xs font-medium mb-2" style={{ color: colors.text.secondary }}>{t('keyFindingsLabel')}</div>
                        <ul className="space-y-1.5">
                          {riskAnalysisData.keyFindings.slice(0, 3).map((f, i) => (
                            <li key={i} className="text-sm flex gap-2" style={{ color: colors.text.primary }}>
                              <span style={{ color: colors.primary[500] }}>•</span>
                              {typeof f === 'string' ? f : (f?.title || f?.summary || '')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {riskAnalysisData.recommendations && riskAnalysisData.recommendations.length > 0 && (
                      <div>
                        <div className="text-xs font-medium mb-2" style={{ color: colors.text.secondary }}>{t('recommendationsLabel')}</div>
                        <ul className="space-y-1.5">
                          {riskAnalysisData.recommendations.slice(0, 2).map((rec, i) => (
                            <li key={i} className="text-sm flex gap-2" style={{ color: colors.text.primary }}>
                              <span style={{ color: colors.success[600] }}>→</span>
                              {typeof rec === 'string' ? rec : (rec?.text || rec?.description || rec)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>

            <Card className="p-6" style={{ borderColor: colors.border.light }}>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>{t('newsTableTitle')}</h3>
                <div className="flex gap-2 flex-wrap">
                  {['all', 'positive', 'negative', 'neutral'].map((key) => (
                    <Button
                      key={key}
                      variant={newsSentimentFilter === key ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewsSentimentFilter(key)}
                    >
                      {key === 'all' ? t('filterAll') : key === 'positive' ? t('sentimentPositive') : key === 'negative' ? t('sentimentNegative') : t('sentimentNeutral')}
                    </Button>
                  ))}
                </div>
              </div>
              {companyNewsLoading ? (
                <p className="py-8 text-center text-sm" style={{ color: colors.text.secondary }}>{t('loadingNews')}</p>
              ) : !filteredNews || filteredNews.length === 0 ? (
                <p className="py-8 text-center text-sm" style={{ color: colors.text.secondary }}>{t('noNews')}</p>
              ) : (
                <div className="max-h-[24rem] overflow-y-auto border rounded-lg" style={{ borderColor: colors.border.light }}>
                  <Table>
                    <TableHeader>
                      <TableRow style={{ borderColor: colors.border.light }}>
                        <TableHead style={{ color: colors.text.secondary }}>{t('newsTableTitleColumn')}</TableHead>
                        <TableHead style={{ color: colors.text.secondary }}>{t('newsTableSnippetColumn')}</TableHead>
                        <TableHead style={{ color: colors.text.secondary }}>{t('newsTableDateColumn')}</TableHead>
                        <TableHead style={{ color: colors.text.secondary }}>{t('newsTableSentimentColumn')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredNews.map((article, i) => (
                        <TableRow key={article.articleId || article.url || i} style={{ borderColor: colors.border.light }}>
                          <TableCell className="max-w-[220px]" style={{ color: colors.text.primary }}>
                            <a href={article.url} target="_blank" rel="noopener noreferrer" className="font-medium text-sm hover:underline truncate block" style={{ color: colors.primary[600] }}>{article.title}</a>
                          </TableCell>
                          <TableCell className="max-w-[280px]" style={{ color: colors.text.secondary }}>
                            <span className="text-xs line-clamp-2 block">{article.description || '—'}</span>
                          </TableCell>
                          <TableCell style={{ color: colors.text.tertiary }}>
                            <span className="text-xs">{article.publishedAt ? (formatRelativeTime(article.publishedAt).relativeKey ? t(formatRelativeTime(article.publishedAt).relativeKey, formatRelativeTime(article.publishedAt).relativeParams) : new Date(article.publishedAt).toLocaleDateString()) : '—'}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={(article.sentiment || 'neutral').toLowerCase() === 'positive' ? 'default' : (article.sentiment || 'neutral').toLowerCase() === 'negative' ? 'destructive' : 'secondary'} className="text-xs">
                              {(article.sentiment || 'neutral').toLowerCase() === 'positive' ? t('sentimentPositive') : (article.sentiment || 'neutral').toLowerCase() === 'negative' ? t('sentimentNegative') : t('sentimentNeutral')}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>

            {/* Routes: map left + single route card right */}
            {!isInWatchlist || !watchlistItem ? (
              <Card className="p-6" style={{ borderColor: colors.border.light }}>
                <div className="py-10 text-center">
                  <p className="text-sm" style={{ color: colors.text.secondary }}>{t('noRoutesToShowOnMap')}</p>
                  <Button className="mt-3" onClick={() => setShowAddToWatchlist(true)}>{t('addToWatchlist')}</Button>
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
                  <div className="lg:col-span-2 flex flex-col">
                    <Card className="p-6 flex flex-col h-[400px]" style={{ borderColor: colors.border.light }}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-semibold" style={{ color: colors.text.primary }}>{t('routesOnMapTitle')}</h3>
                        <button type="button" onClick={() => setMapRefreshTrigger((prev) => prev + 1)} className="p-2 rounded-lg transition-colors hover:opacity-80" style={{ color: colors.text.secondary }} aria-label={t('refreshData')}>
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg">
                        {watchlistItem.userCompanyInfo?.routes?.length > 0 && (watchlistItem.userCompanyInfo?.allAddressesForRouteDisplay?.length > 0 || watchlistItem.userCompanyInfo?.userAddresses?.length > 0) ? (
                          <SupplyChainMap
                            markers={markers}
                            routes={routes}
                            bounds={bounds}
                            geocoding={geocoding}
                            compact
                            minHeight={320}
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center text-center p-6">
                            <MapIcon className="h-12 w-12 mx-auto mb-3" style={{ color: colors.gray[400] }} />
                            <p className="text-sm" style={{ color: colors.text.secondary }}>{t('noRoutesToShowOnMap')}</p>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                  <div className="flex flex-col h-[400px]">
                    <Card className="p-4 flex flex-col h-full min-h-0" style={{ borderColor: colors.border.light }}>
                      {(() => {
                        const routesList = watchlistItem.userCompanyInfo?.routes ?? [];
                        const addressesForRoutesOverview = watchlistItem.userCompanyInfo?.allAddressesForRouteDisplay ?? watchlistItem.userCompanyInfo?.userAddresses ?? [];
                        const safeIndex = routesList.length ? Math.min(selectedRouteIndex, routesList.length - 1) : 0;
                        const route = routesList[safeIndex];
                        const addressMapOverview = new Map();
                        addressesForRoutesOverview.forEach((addr) => { const id = addr.addressId ?? addr.address_id; if (id) addressMapOverview.set(id, addr); });
                        const getRouteAddressesOverview = (r) => {
                          if (!r) return { startDisplay: '', endDisplay: '', startShort: '', endShort: '' };
                          const startAddr = addressMapOverview.get(r.startPointAddressId ?? r.start_point_address_id);
                          const endAddr = addressMapOverview.get(r.endPointAddressId ?? r.end_point_address_id);
                          return {
                            startDisplay: startAddr?.getFullAddress?.() ?? [startAddr?.streetAddress ?? startAddr?.street_address, startAddr?.city, startAddr?.stateProvince ?? startAddr?.state_province, startAddr?.country].filter(Boolean).join(', ') ?? '—',
                            endDisplay: endAddr?.getFullAddress?.() ?? [endAddr?.streetAddress ?? endAddr?.street_address, endAddr?.city, endAddr?.stateProvince ?? endAddr?.state_province, endAddr?.country].filter(Boolean).join(', ') ?? '—',
                            startShort: startAddr?.getShortAddress?.() ?? startAddr?.getFullAddress?.() ?? [startAddr?.streetAddress ?? startAddr?.street_address, startAddr?.city].filter(Boolean).join(', ') ?? '—',
                            endShort: endAddr?.getShortAddress?.() ?? endAddr?.getFullAddress?.() ?? [endAddr?.streetAddress ?? endAddr?.street_address, endAddr?.city].filter(Boolean).join(', ') ?? '—',
                          };
                        };
                        const onlyOneRoute = routesList.length <= 1;
                        return (
                          <>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold" style={{ color: colors.text.primary }}>{t('routesTitle')}</h3>
                              <div className="flex items-center gap-1">
                                <Button variant="outline" size="icon-sm" onClick={() => setSelectedRouteIndex((i) => Math.max(0, i - 1))} disabled={onlyOneRoute || safeIndex <= 0} aria-label={t('previousRoute')}>
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon-sm" onClick={() => setSelectedRouteIndex((i) => Math.min(routesList.length - 1, i + 1))} disabled={onlyOneRoute || safeIndex >= routesList.length - 1} aria-label={t('nextRoute')}>
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            {route ? (
                              <div className="flex-1 min-h-0 overflow-y-auto">
                                <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: route.isActive !== false ? colors.success[300] : colors.gray[300], backgroundColor: route.isActive !== false ? colors.success[50] : colors.gray[50] }}>
                                  <div className="p-4 flex items-start justify-between" style={{ backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100] }}>
                                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: route.isActive !== false ? colors.success[500] : colors.gray[500] }}>
                                        <Navigation className="h-5 w-5 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm truncate" style={{ color: colors.text.primary }}>{route.routeName || route.route_name || t('routesTitle')}</h3>
                                      </div>
                                    </div>
                                    <span className="px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2" style={{ backgroundColor: route.isActive !== false ? colors.success[200] : colors.gray[200], color: route.isActive !== false ? colors.success[700] : colors.gray[700] }}>
                                      {route.isActive !== false ? t('active') : t('routeDetailsInactive')}
                                    </span>
                                  </div>
                                  <div className="p-4 space-y-3">
                                    <div className="flex items-start space-x-3">
                                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.success[200] }}>
                                        <MapPin className="h-4 w-4" style={{ color: colors.success[700] }} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>{t('routeDetailsStartingPoint')}</p>
                                        <p className="text-sm font-semibold mt-0.5" style={{ color: colors.text.primary }}>{getRouteAddressesOverview(route).startShort}</p>
                                        {getRouteAddressesOverview(route).startDisplay !== getRouteAddressesOverview(route).startShort && (
                                          <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>{getRouteAddressesOverview(route).startDisplay}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-center">
                                      <div className="w-full h-0.5" style={{ backgroundColor: colors.border.light }} />
                                      <div className="mx-2 px-2 py-1 rounded-full" style={{ backgroundColor: colors.gray[200] }}>
                                        <span className="text-xs font-bold" style={{ color: colors.text.secondary }}>→</span>
                                      </div>
                                      <div className="w-full h-0.5" style={{ backgroundColor: colors.border.light }} />
                                    </div>
                                    <div className="flex items-start space-x-3">
                                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.danger[200] }}>
                                        <MapPin className="h-4 w-4" style={{ color: colors.danger[700] }} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>{t('routeDetailsDestination')}</p>
                                        <p className="text-sm font-semibold mt-0.5" style={{ color: colors.text.primary }}>{getRouteAddressesOverview(route).endShort}</p>
                                        {getRouteAddressesOverview(route).endDisplay !== getRouteAddressesOverview(route).endShort && (
                                          <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>{getRouteAddressesOverview(route).endDisplay}</p>
                                        )}
                                      </div>
                                    </div>
                                    <Button className="w-full mt-3" size="sm" onClick={() => setShowRouteDetailsForRouteId(route.routeId ?? route.route_id)} style={{ backgroundColor: colors.primary[500], color: '#fff' }}>
                                      <MapIcon className="h-4 w-4 mr-2" />
                                      {t('routeDetailsViewAnalysis')}
                                      <ExternalLink className="h-3 w-3 ml-2" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex-1 flex items-center justify-center text-center p-6">
                                <p className="text-sm" style={{ color: colors.text.secondary }}>{t('noRoutesDefined')}</p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </Card>
                  </div>
                </div>
            )}
          </TabsContent>

          <TabsContent value="risk" className="space-y-4 w-full max-w-full">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="space-y-4">
                <Card className="p-6" style={{ borderColor: colors.border.light }}>
                  <h3 className="text-lg font-semibold mb-4" style={{ color: colors.text.primary }}>{t('relatedNews')}</h3>
                  {(!displayNews || displayNews.length === 0) ? <p className="text-sm py-2" style={{ color: colors.text.secondary }}>{t('noRelatedNews')}</p> : (
                    <div className="max-h-[28rem] overflow-y-auto pr-1 -mr-1">
                      <ul className="space-y-4">
                        {displayNews.map((article, i) => (
                          <li key={article.articleId || i} className="pb-4 border-b last:border-0 last:pb-0" style={{ borderColor: colors.border.light }}>
                            <a href={article.url} target="_blank" rel="noopener noreferrer" className="font-medium text-sm hover:underline block mb-1" style={{ color: colors.primary[600] }}>{article.title}</a>
                            <p className="text-xs leading-relaxed line-clamp-2 mb-1" style={{ color: colors.text.secondary }}>{article.description || ''}</p>
                            <span className="text-xs" style={{ color: colors.text.tertiary }}>{article.publishedAt ? (formatRelativeTime(article.publishedAt).relativeKey ? t(formatRelativeTime(article.publishedAt).relativeKey, formatRelativeTime(article.publishedAt).relativeParams) : new Date(article.publishedAt).toLocaleDateString()) : ''}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </Card>
                <Card className="p-6" style={{ borderColor: colors.border.light }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>{t('riskDrivers')}</h3>
                    <span className="text-sm font-medium" style={{ color: colors.text.secondary }}>{t('riskDriversActiveCount', { count: riskDriversCount })}</span>
                  </div>
                  {riskDriversCount === 0 ? <p className="text-sm py-2" style={{ color: colors.text.secondary }}>{t('noRiskAnalysis')}</p> : (
                    <ul className="space-y-3">
                      {individualScores.slice(0, 8).map((r, i) => {
                        const impact = (r.impactLevel ?? r.impact_level) || 'medium';
                        const borderColor = impact === 'high' ? colors.danger[300] : impact === 'medium' ? colors.warning[300] : colors.success[300];
                        return (
                          <li key={i} className="flex gap-3 p-3 rounded-lg border-l-4" style={{ backgroundColor: colors.gray[50], borderLeftColor: borderColor }}>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm mb-0.5" style={{ color: colors.text.primary }}>{(r.riskName ?? r.risk_name ?? r.category) || 'Risk'}</div>
                              {(r.reasoning || r.evidence?.[0]) && <p className="text-xs leading-relaxed" style={{ color: colors.text.secondary }}>{r.reasoning || r.evidence[0]}</p>}
                            </div>
                            <Badge variant={impact === 'high' ? 'destructive' : 'secondary'} className="shrink-0">{impact === 'high' ? t('critical') : impact === 'medium' ? t('warning') : t('monitor')}</Badge>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Card>
              </div>
              <Card className="p-6" style={{ borderColor: colors.border.light }}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: colors.text.primary }}>{t('riskScore')}</h3>
                  <RiskAnalysisButton ticker={ticker} companyName={company.name} onAnalysisComplete={handleRiskAnalysisComplete} onError={handleRiskAnalysisError} variant="primary" />
                </div>
                {!riskAnalysisData ? (
                  <p className="text-sm py-4" style={{ color: colors.text.secondary }}>{t('noRiskAnalysis')}</p>
                ) : (
                  <div className="space-y-5">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-bold" style={{ color: getScoreColor(riskAnalysisData.overallRiskScore) }}>{(riskAnalysisData.overallRiskScore / 10).toFixed(1)}</span>
                      <span className="text-sm" style={{ color: colors.text.secondary }}>/ 10</span>
                      <span className="text-xs font-medium ml-2 px-2 py-0.5 rounded" style={{ backgroundColor: colors.primary[50], color: colors.primary[700] }}>{riskAnalysisData.getFormattedAnalysisDate?.() || new Date(riskAnalysisData.analysisDate).toLocaleDateString()}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: colors.gray[50] }}>
                        <div className="text-xs font-medium mb-0.5" style={{ color: colors.text.secondary }}>{t('supplyChainRisk')}</div>
                        <div className="text-lg font-semibold" style={{ color: getScoreColor(riskAnalysisData.supplyChainRiskScore) }}>{(riskAnalysisData.supplyChainRiskScore / 10).toFixed(1)}</div>
                      </div>
                      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: colors.gray[50] }}>
                        <div className="text-xs font-medium mb-0.5" style={{ color: colors.text.secondary }}>{t('industryRisk')}</div>
                        <div className="text-lg font-semibold" style={{ color: getScoreColor(riskAnalysisData.industryRiskScore) }}>{(riskAnalysisData.industryRiskScore / 10).toFixed(1)}</div>
                      </div>
                      <div className="p-3 rounded-lg text-center" style={{ backgroundColor: colors.gray[50] }}>
                        <div className="text-xs font-medium mb-0.5" style={{ color: colors.text.secondary }}>{t('newsSentimentLabel')}</div>
                        <div className="text-lg font-semibold" style={{ color: getScoreColor(riskAnalysisData.newsSentimentScore) }}>{(riskAnalysisData.newsSentimentScore / 10).toFixed(1)}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-4 py-2 border-t border-b" style={{ borderColor: colors.border.light }}>
                      <span className="text-xs" style={{ color: colors.text.secondary }}>{t('analyzedArticlesCount', { count: riskAnalysisData.analyzedNewsCount || 0 })}</span>
                      <span className="text-xs" style={{ color: colors.success[600] }}>+{riskAnalysisData.positiveNewsCount || 0}</span>
                      <span className="text-xs" style={{ color: colors.danger[600] }}>−{riskAnalysisData.negativeNewsCount || 0}</span>
                      <span className="text-xs" style={{ color: colors.text.tertiary }}>○{riskAnalysisData.neutralNewsCount || 0}</span>
                      <span className="text-xs" style={{ color: colors.text.secondary }}>Confidence: <strong style={{ color: colors.text.primary }}>{Math.round(riskAnalysisData.analysisConfidence || 0)}%</strong></span>
                      <span className="text-xs" style={{ color: colors.text.secondary }}>Data quality: <strong style={{ color: colors.text.primary }}>{Math.round(riskAnalysisData.dataQualityScore || 0)}%</strong></span>
                    </div>
                    {riskAnalysisData.analysisSummary && (
                      <div>
                        <div className="text-xs font-medium mb-1" style={{ color: colors.text.secondary }}>{t('summaryLabel')}</div>
                        <p className="text-sm leading-relaxed" style={{ color: colors.text.primary }}>{riskAnalysisData.analysisSummary}</p>
                      </div>
                    )}
                    {riskAnalysisData.keyFindings && riskAnalysisData.keyFindings.length > 0 && (
                      <div>
                        <div className="text-xs font-medium mb-2" style={{ color: colors.text.secondary }}>{t('keyFindingsLabel')}</div>
                        <ul className="space-y-1.5">
                          {riskAnalysisData.keyFindings.slice(0, 4).map((f, i) => (
                            <li key={i} className="text-sm flex gap-2" style={{ color: colors.text.primary }}>
                              <span style={{ color: colors.primary[500] }}>•</span>
                              {typeof f === 'string' ? f : (f?.title || f?.summary || '')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {riskAnalysisData.recommendations && riskAnalysisData.recommendations.length > 0 && (
                      <div>
                        <div className="text-xs font-medium mb-2" style={{ color: colors.text.secondary }}>{t('recommendationsLabel')}</div>
                        <ul className="space-y-1.5">
                          {riskAnalysisData.recommendations.slice(0, 3).map((rec, i) => (
                            <li key={i} className="text-sm flex gap-2" style={{ color: colors.text.primary }}>
                              <span style={{ color: colors.success[600] }}>→</span>
                              {typeof rec === 'string' ? rec : (rec?.text || rec?.description || rec)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="news" className="space-y-4 w-full max-w-full">
            <Card className="p-6" style={{ borderColor: colors.border.light }}>
              <RiskIntelligenceFeed
                articles={displayNews || []}
                loading={companyNewsLoading}
                showHeader={false}
                compact={true}
                inlineTitle={t('newsTitle')}
                search={newsTabSearch}
                onSearchChange={setNewsTabSearch}
                selectedTopic="all"
                selectedRegion="all"
                selectedSeverity="all"
                quickFilterCounts={{
                  allNews: displayNews?.length ?? 0,
                  positiveCount: (displayNews || []).filter((a) => getArticleSentiment(a) === 'positive').length,
                  negativeCount: (displayNews || []).filter((a) => getArticleSentiment(a) === 'negative').length,
                  savedArticles: savedArticleIds.size,
                }}
                activeQuickFilter={newsTabQuickFilter}
                onQuickFilterChange={setNewsTabQuickFilter}
                trendingTopics={[]}
                hasActiveFilters={!!newsTabSearch.trim()}
                onClearFilters={() => setNewsTabSearch('')}
                showSentimentFilter={false}
                selectedSentiment="all"
                onSentimentChange={() => {}}
                onSaveArticle={handleSaveArticle}
                savedArticleIds={savedArticleIds}
                showWatchlistQuickFilter={false}
                showAlertSettings={false}
                showSidebar={false}
                compactFiltersOnly={true}
              />
            </Card>
          </TabsContent>

          <TabsContent value="routes" className="space-y-4 w-full max-w-full">
            {!isInWatchlist || !watchlistItem ? (
              <Card className="p-6" style={{ borderColor: colors.border.light }}>
                <div className="py-10 text-center">
                  <p className="mb-4 text-sm" style={{ color: colors.text.secondary }}>{t('addToWatchlistToManageRoutes')}</p>
                  <Button onClick={() => setShowAddToWatchlist(true)}>{t('addToWatchlist')}</Button>
                </div>
              </Card>
            ) : (
                <>
                <RoutesTabAddressCard
                  companyId={ticker}
                  addresses={watchlistItem.userCompanyInfo?.userAddresses ?? []}
                  onAddressAdded={fetchCompanyData}
                  onOpenCreateRoute={() => setShowRouteModal(true)}
                />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:items-stretch">
                  <div className="lg:col-span-2 flex flex-col">
                    <Card className="p-6 flex flex-col h-[400px]" style={{ borderColor: colors.border.light }}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-semibold" style={{ color: colors.text.primary }}>{t('routesOnMapTitle')}</h3>
                        <button
                          type="button"
                          onClick={() => setMapRefreshTrigger((prev) => prev + 1)}
                          className="p-2 rounded-lg transition-colors hover:opacity-80"
                          style={{ color: colors.text.secondary }}
                          aria-label={t('refreshData')}
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="relative min-h-0 flex-1 overflow-hidden rounded-lg">
                        {watchlistItem.userCompanyInfo?.routes?.length > 0 && (watchlistItem.userCompanyInfo?.allAddressesForRouteDisplay?.length > 0 || watchlistItem.userCompanyInfo?.userAddresses?.length > 0) ? (
                          <SupplyChainMap
                            markers={markers}
                            routes={routes}
                            bounds={bounds}
                            geocoding={geocoding}
                            compact
                            minHeight={320}
                          />
                        ) : (
                          <div className="h-full flex items-center justify-center text-center p-6">
                            <MapIcon className="h-12 w-12 mx-auto mb-3" style={{ color: colors.gray[400] }} />
                            <p className="text-sm" style={{ color: colors.text.secondary }}>{t('noRoutesToShowOnMap')}</p>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                  <div className="flex flex-col h-[400px]">
                    <Card className="p-4 flex flex-col h-full min-h-0" style={{ borderColor: colors.border.light }}>
                      {(() => {
                        const routesList = watchlistItem.userCompanyInfo?.routes ?? [];
                        const addressesForRoutes = watchlistItem.userCompanyInfo?.allAddressesForRouteDisplay ?? watchlistItem.userCompanyInfo?.userAddresses ?? [];
                        const safeIndex = routesList.length ? Math.min(selectedRouteIndex, routesList.length - 1) : 0;
                        const route = routesList[safeIndex];
                        const addressMap = new Map();
                        addressesForRoutes.forEach((addr) => {
                          const id = addr.addressId ?? addr.address_id;
                          if (id) addressMap.set(id, addr);
                        });
                        const getRouteAddresses = (r) => {
                          if (!r) return { startDisplay: '', endDisplay: '', startShort: '', endShort: '' };
                          const startAddr = addressMap.get(r.startPointAddressId ?? r.start_point_address_id);
                          const endAddr = addressMap.get(r.endPointAddressId ?? r.end_point_address_id);
                          return {
                            startDisplay: startAddr?.getFullAddress?.() ?? r.startPointAddressId ?? '—',
                            endDisplay: endAddr?.getFullAddress?.() ?? r.endPointAddressId ?? '—',
                            startShort: startAddr?.getShortAddress?.() ?? startAddr?.getFullAddress?.() ?? '—',
                            endShort: endAddr?.getShortAddress?.() ?? endAddr?.getFullAddress?.() ?? '—',
                          };
                        };
                        const openInGoogleMaps = (r) => {
                          const { startDisplay, endDisplay } = getRouteAddresses(r);
                          window.open(getGoogleMapsRouteUrl(startDisplay, endDisplay), '_blank');
                        };
                        const onlyOneRoute = routesList.length <= 1;
                        return (
                          <>
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold" style={{ color: colors.text.primary }}>{t('routesTitle')}</h3>
                              <div className="flex items-center gap-1">
                                <Button variant="outline" size="icon-sm" onClick={() => setSelectedRouteIndex((i) => Math.max(0, i - 1))} disabled={onlyOneRoute || safeIndex <= 0} aria-label={t('paginationPrevious')}>
                                  <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <Button variant="outline" size="icon-sm" onClick={() => setSelectedRouteIndex((i) => Math.min(routesList.length - 1, i + 1))} disabled={onlyOneRoute || safeIndex >= routesList.length - 1} aria-label={t('paginationNext')}>
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            {route ? (
                              <div className="flex-1 min-h-0 overflow-y-auto">
                                <div className="rounded-xl border-2 overflow-hidden" style={{ borderColor: route.isActive !== false ? colors.success[300] : colors.gray[300], backgroundColor: route.isActive !== false ? colors.success[50] : colors.gray[50] }}>
                                  <div className="p-4 flex items-start justify-between" style={{ backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100] }}>
                                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: route.isActive !== false ? colors.success[500] : colors.gray[500] }}>
                                        <Navigation className="h-5 w-5 text-white" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold text-sm truncate" style={{ color: colors.text.primary }}>{route.routeName || route.route_name || t('routesTitle')}</h3>
                                      </div>
                                    </div>
                                    <span className="px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2" style={{ backgroundColor: route.isActive !== false ? colors.success[200] : colors.gray[200], color: route.isActive !== false ? colors.success[700] : colors.gray[700] }}>
                                      {route.isActive !== false ? t('active') : t('routeDetailsInactive')}
                                    </span>
                                  </div>
                                  <div className="p-4 space-y-3">
                                    <div className="flex items-start space-x-3">
                                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.success[200] }}>
                                        <MapPin className="h-4 w-4" style={{ color: colors.success[700] }} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>{t('routeDetailsStartingPoint')}</p>
                                        <p className="text-sm font-semibold mt-0.5" style={{ color: colors.text.primary }}>{getRouteAddresses(route).startShort}</p>
                                        {getRouteAddresses(route).startDisplay !== getRouteAddresses(route).startShort && (
                                          <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>{getRouteAddresses(route).startDisplay}</p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center justify-center">
                                      <div className="w-full h-0.5" style={{ backgroundColor: colors.border.light }} />
                                      <div className="mx-2 px-2 py-1 rounded-full" style={{ backgroundColor: colors.gray[200] }}>
                                        <span className="text-xs font-bold" style={{ color: colors.text.secondary }}>→</span>
                                      </div>
                                      <div className="w-full h-0.5" style={{ backgroundColor: colors.border.light }} />
                                    </div>
                                    <div className="flex items-start space-x-3">
                                      <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.danger[200] }}>
                                        <MapPin className="h-4 w-4" style={{ color: colors.danger[700] }} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>{t('routeDetailsDestination')}</p>
                                        <p className="text-sm font-semibold mt-0.5" style={{ color: colors.text.primary }}>{getRouteAddresses(route).endShort}</p>
                                        {getRouteAddresses(route).endDisplay !== getRouteAddresses(route).endShort && (
                                          <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>{getRouteAddresses(route).endDisplay}</p>
                                        )}
                                      </div>
                                    </div>
                                    <Button className="w-full mt-3" size="sm" onClick={() => setShowRouteDetailsForRouteId(route.routeId ?? route.route_id)} style={{ backgroundColor: colors.primary[500], color: '#fff' }}>
                                      <MapIcon className="h-4 w-4 mr-2" />
                                      {t('routeDetailsViewAnalysis')}
                                      <ExternalLink className="h-3 w-3 ml-2" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex-1 flex items-center justify-center text-center p-6">
                                <p className="text-sm" style={{ color: colors.text.secondary }}>{t('noRoutesDefined')}</p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </Card>
                  </div>
                </div>
                </>
            )}
          </TabsContent>
        </Tabs>

      </div>

      {/* Modals */}
      {showAddToWatchlist && (
        <AddToWatchlistSimpleModal
          company={company}
          onClose={() => setShowAddToWatchlist(false)}
          onSubmit={handleAddToWatchlist}
        />
      )}

      {showEditWatchlist && watchlistItem && (
        <EditWatchlistItemModal
          item={watchlistItem}
          open={showEditWatchlist}
          onClose={() => setShowEditWatchlist(false)}
          onSubmit={handleUpdateWatchlistItem}
        />
      )}

      {showEditCompany && (editCompany || company) ? (
        <EditCompanyModal
          company={editCompany || company}
          open={showEditCompany}
          userScoped
          onClose={handleCloseEditSupplier}
          onSubmit={handleUpdateCompanyProfile}
        />
      ) : null}

      {showAddressModal && (
        <AddressManagementModal
          companyId={ticker}
          onClose={() => { setShowAddressModal(false); fetchCompanyData(); }}
          onAddressAdded={() => fetchCompanyData()}
        />
      )}

      {showRouteModal && (
        <RouteManagementModal
          companyId={ticker}
          watchlistItems={watchlistItemsForRouteModal}
          onClose={() => { setShowRouteModal(false); fetchCompanyData(); }}
          onRouteAdded={() => fetchCompanyData()}
        />
      )}

      {/* Route details / View analysis dialog */}
      {showRouteDetailsForRouteId && watchlistItem?.userCompanyInfo && (() => {
        const routesList = watchlistItem.userCompanyInfo.routes ?? [];
        const addressesForRoutes = watchlistItem.userCompanyInfo.allAddressesForRouteDisplay ?? watchlistItem.userCompanyInfo.userAddresses ?? [];
        const route = routesList.find((r) => (r.routeId ?? r.route_id) === showRouteDetailsForRouteId);
        const analysis = routeAnalyses[showRouteDetailsForRouteId];
        const addressMap = new Map();
        addressesForRoutes.forEach((addr) => {
          const id = addr.addressId ?? addr.address_id;
          if (id) addressMap.set(id, addr);
        });
        const getRouteAddresses = (r) => {
          if (!r) return { startDisplay: '', endDisplay: '', startShort: '', endShort: '' };
          const startAddr = addressMap.get(r.startPointAddressId ?? r.start_point_address_id);
          const endAddr = addressMap.get(r.endPointAddressId ?? r.end_point_address_id);
          const startLine = startAddr?.getFullAddress?.() ?? [startAddr?.streetAddress ?? startAddr?.street_address, startAddr?.city, startAddr?.stateProvince ?? startAddr?.state_province, startAddr?.country].filter(Boolean).join(', ') ?? '—';
          const endLine = endAddr?.getFullAddress?.() ?? [endAddr?.streetAddress ?? endAddr?.street_address, endAddr?.city, endAddr?.stateProvince ?? endAddr?.state_province, endAddr?.country].filter(Boolean).join(', ') ?? '—';
          return {
            startDisplay: startLine,
            endDisplay: endLine,
            startShort: startAddr?.getShortAddress?.() ?? startLine,
            endShort: endAddr?.getShortAddress?.() ?? endLine,
          };
        };
        const openInGoogleMaps = (r) => {
          const { startDisplay, endDisplay } = getRouteAddresses(r);
          window.open(getGoogleMapsRouteUrl(startDisplay, endDisplay), '_blank');
        };
        if (analysis) {
          return (
            <RouteDetailsDialog
              routeAnalysis={analysis}
              onClose={() => setShowRouteDetailsForRouteId(null)}
              onAnalyze={handleAnalyzeRoute}
              analyzing={analyzingRoutes[showRouteDetailsForRouteId]}
            />
          );
        }
        if (!route) return null;
        const { startDisplay, endDisplay, startShort, endShort } = getRouteAddresses(route);
        return (
          <>
            <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: theme.zIndex.overlay }} onClick={() => setShowRouteDetailsForRouteId(null)} aria-hidden />
            <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg max-h-[90vh] overflow-hidden rounded-xl shadow-xl flex flex-col" style={{ backgroundColor: colors.background.card, zIndex: theme.zIndex.modal, border: `1px solid ${colors.border.light}` }} role="dialog" aria-modal="true" aria-labelledby="route-details-title">
              <div className="flex items-center justify-between flex-shrink-0 px-5 py-4 border-b" style={{ borderColor: colors.border.light }}>
                <h2 id="route-details-title" className="text-lg font-semibold" style={{ color: colors.text.primary }}>{t('routeDetailsTitle')}</h2>
                <button type="button" onClick={() => setShowRouteDetailsForRouteId(null)} className="p-2 rounded-lg transition-colors hover:opacity-80" style={{ color: colors.text.secondary }} aria-label={t('close')}>
                  <span className="text-xl leading-none">×</span>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100] }}>
                    <Navigation className="h-5 w-5" style={{ color: route.isActive !== false ? colors.success[600] : colors.gray[600] }} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base" style={{ color: colors.text.primary }}>{route.routeName ?? route.route_name ?? t('routesTitle')}</h3>
                    <span className="text-xs font-medium px-2 py-0.5 rounded" style={{ backgroundColor: route.isActive !== false ? colors.success[100] : colors.gray[100], color: route.isActive !== false ? colors.success[700] : colors.gray[600] }}>
                      {route.isActive !== false ? t('active') : t('routeDetailsInactive')}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.success[100] }}>
                      <MapPin className="h-4 w-4" style={{ color: colors.success[700] }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium" style={{ color: colors.text.tertiary }}>{t('routeDetailsStartingPoint')}</p>
                      <p className="text-sm font-medium mt-0.5" style={{ color: colors.text.primary }}>{startShort || startDisplay || '—'}</p>
                      {startDisplay && startDisplay !== startShort && <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>{startDisplay}</p>}
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.danger[100] }}>
                      <MapPin className="h-4 w-4" style={{ color: colors.danger[700] }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium" style={{ color: colors.text.tertiary }}>{t('routeDetailsDestination')}</p>
                      <p className="text-sm font-medium mt-0.5" style={{ color: colors.text.primary }}>{endShort || endDisplay || '—'}</p>
                      {endDisplay && endDisplay !== endShort && <p className="text-xs mt-0.5 truncate" style={{ color: colors.text.tertiary }}>{endDisplay}</p>}
                    </div>
                  </div>
                </div>
                <p className="text-sm" style={{ color: colors.text.secondary }}>{t('routeDetailsViewAnalysisNoData')}</p>
              </div>
              <div className="flex flex-wrap items-center justify-end gap-2 flex-shrink-0 px-5 py-4 border-t" style={{ borderColor: colors.border.light, backgroundColor: colors.gray[50] }}>
                <Button variant="outline" size="sm" onClick={() => handleAnalyzeRoute(showRouteDetailsForRouteId)} disabled={analyzingRoutes[showRouteDetailsForRouteId]}>
                  {analyzingRoutes[showRouteDetailsForRouteId] ? t('routeDetailsAnalyzing') : t('routeDetailsRefreshAnalysis')}
                </Button>
                <Button size="sm" onClick={() => openInGoogleMaps(route)} style={{ backgroundColor: colors.primary[500], color: colors.text.inverse }}>
                  <ExternalLink className="h-4 w-4 mr-1.5" />
                  {t('routeDetailsViewInMaps')}
                </Button>
              </div>
            </div>
          </>
        );
      })()}

      {/* Remove Confirmation Modal */}
      {showRemoveConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={() => setShowRemoveConfirmation(false)} />
            
            <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                    <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                </div>
                
                <div className="text-center">
                  <h3 className="text-lg font-medium mb-2" style={{ color: colors.text.primary }}>{t('removeFromWatchlistConfirmTitle')}</h3>
                  <p className="text-sm mb-6" style={{ color: colors.text.secondary }}>{t('removeFromWatchlistConfirmMessage', { name: company.name })}</p>
                  <div className="flex gap-3 justify-center">
                    <Button variant="outline" onClick={() => setShowRemoveConfirmation(false)}>{t('cancel')}</Button>
                    <Button variant="destructive" onClick={() => { handleRemoveFromWatchlist(); setShowRemoveConfirmation(false); }}>{t('remove')}</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Analysis Modal */}
      <RiskAnalysisModal
        isOpen={showRiskAnalysisModal}
        onClose={handleCloseRiskAnalysisModal}
        ticker={ticker}
        companyName={company?.name}
        initialAnalysis={riskAnalysis || company?.latestRiskAnalysis}
      />
    </div>
  );
};

export default CompanyDetails;
